import { Link } from 'react-router';
import { LegalPage } from '../../components/LegalPage';
import { CONTACT_EMAIL, OPERATOR } from '../../lib/legal';

const mail = <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>;

export function DeveloperTerms() {
  return (
    <LegalPage
      title="Developer Terms"
      summary={
        <ul className="list-disc space-y-1 pl-5">
          <li>Developers must be 18 or older, and approved by us.</li>
          <li>Your games stay yours. You let us host them, show them and promote them on MOGZO.</li>
          <li>Games must be suitable for players aged 13 and up, and follow our content rules.</li>
          <li>Use player data only to run your game on MOGZO, and keep your account secure.</li>
          <li>
            Each version you send for review comes with its source code, which only reviewers see.
          </li>
          <li>Publishing is free during early access.</li>
        </ul>
      }
    >
      <h2>1. About these terms</h2>
      <p>
        These Developer Terms apply when you apply to become, or are, a MOGZO developer partner.
        They add to our <Link to="/terms">Terms of Service</Link> and{' '}
        <Link to="/privacy">Privacy Policy</Link>, which also apply to you. If these terms and the
        Terms of Service disagree about developer matters, these terms win.
      </p>

      <h2>2. Who can be a developer</h2>
      <ul>
        <li>You must be at least 18 years old.</li>
        <li>
          If you apply for a studio, team or company, you confirm you are allowed to accept these
          terms for it.
        </li>
        <li>
          Information in your application must be true, and the work you link to must be yours or
          your team’s.
        </li>
        <li>
          We review every application by hand. We may approve or decline an application at our
          discretion. If we decline, you can apply again after 30 days.
        </li>
      </ul>

      <h2>3. Your developer account</h2>
      <ul>
        <li>Two-factor authentication is required for developer accounts.</li>
        <li>
          Keep your password, two-factor codes and any tokens for MOGZO tools secret. You are
          responsible for what happens under your developer account and your projects.
        </li>
        <li>Tell us at {mail} straight away if you think someone else has used them.</li>
      </ul>

      <h2>4. Your games and content</h2>
      <p>
        You keep ownership of your games and everything in them. By uploading a game to MOGZO, you
        give {OPERATOR} a worldwide, non-exclusive, royalty-free licence to host, run, copy, show
        and distribute it on MOGZO, and to use its name, images and short clips to promote it on
        MOGZO and in MOGZO’s own marketing. The licence ends when you remove the game, except for
        copies in backups for a limited time and posts players have already made.
      </p>
      <p>You promise that:</p>
      <ul>
        <li>
          you have the rights to everything in your game, including code, art, music, fonts and
          trademarks;
        </li>
        <li>your game doesn’t break any law or anyone else’s rights.</li>
      </ul>

      <h2>5. Content rules</h2>
      <p>MOGZO is for players aged 13 and up. Games must not include:</p>
      <ul>
        <li>sexual content or nudity, or anything that sexualises minors;</li>
        <li>graphic violence or gore meant to shock;</li>
        <li>hate, harassment, or content promoting violence or self-harm;</li>
        <li>gambling with real money or items that can be cashed out;</li>
        <li>malware, crypto mining, phishing, or code that tracks players outside your game;</li>
        <li>ads, or links that take players to buy things outside MOGZO, unless we allow it.</li>
      </ul>
      <p>
        We may review your games before and after they are published, and we may hide or remove a
        game that breaks these rules.
      </p>

      <h2>6. Review and your source code</h2>
      <ul>
        <li>
          Every version you send for review comes with a zip of its source code, so our reviewers
          can check the code behind the game. Only MOGZO’s reviewers open it, and we never publish
          it or use it for anything else.
        </li>
        <li>
          We keep the source of the live version, any version in review and the two approved
          versions before the live one. We delete the rest 90 days after we decide on that version.
        </li>
        <li>
          Don’t put passwords, keys or other secrets in your source. Our checks refuse a version
          when they find some.
        </li>
        <li>
          Before a person reviews it, MOGZO scans each version automatically, including for viruses
          and code that loads things from sites you haven’t declared.
        </li>
      </ul>

      <h2>7. Player data</h2>
      <ul>
        <li>
          Use the player data MOGZO gives your game (such as display names, saves and scores) only
          to run your game on MOGZO.
        </li>
        <li>Don’t sell it, share it, or try to find out who players are outside MOGZO.</li>
        <li>
          Don’t ask players for personal details such as their real name, email, phone number or
          location inside your game.
        </li>
      </ul>

      <h2>8. Fees</h2>
      <p>
        Publishing on MOGZO is free during early access. If we later offer paid features or ways for
        developers to earn money, they will come with their own terms, and we won’t charge you for
        anything you haven’t agreed to.
      </p>

      <h2>9. Suspension and ending</h2>
      <p>
        You can stop being a developer at any time by writing to {mail}. We may suspend or end your
        developer access, and remove your games, if you break these terms, if your games put players
        at risk, or if we stop the developer programme. We will tell you why, unless the law or
        safety stops us.
      </p>

      <h2>10. Early access</h2>
      <p>
        MOGZO and its developer tools are in early access. Features may change, break or be removed,
        and we provide them “as is”. The limits of our liability in our{' '}
        <Link to="/terms">Terms of Service</Link> also apply to developers.
      </p>

      <h2>11. Changes and contact</h2>
      <p>
        We may update these terms. If a change matters, we will email developers before it takes
        effect. Questions: {mail}.
      </p>
    </LegalPage>
  );
}
